import React, { useState, useEffect } from 'react';
import { useFonts, Arvo_700Bold } from '@expo-google-fonts/arvo';
import { View, Text, TextInput, TouchableOpacity, StyleSheet, ActivityIndicator, Alert, ScrollView, KeyboardAvoidingView, Platform, Image, Linking } from 'react-native';
import { StatusBar } from 'expo-status-bar';
import { signInWithGoogle, signInWithApple, signInWithAppleWeb, signInWithEmail, signUpWithEmail, resetPassword, statusCodes, handleWebRedirectResult } from '../firebase/auth';

let AppleAuthentication = null;
try { AppleAuthentication = require('expo-apple-authentication'); } catch {}

const BURGUNDY = '#4A0E0E';
const GOLD = '#D4AF37';
const CREAM = '#F5EDD8';
const RED = '#C62828';
const BG = '#FAF8F4';
const TEXT = '#1A1A1A';
const MUTED = '#757575';
const BORDER = '#EAE4DA';

const FRIENDLY_ERRORS = {
  'auth/invalid-email': 'That email address looks invalid.',
  'auth/user-not-found': 'No account found with that email.',
  'auth/wrong-password': 'Incorrect password.',
  'auth/invalid-credential': 'Incorrect email or password.',
  'auth/email-already-in-use': 'An account with that email already exists.',
  'auth/weak-password': 'Password should be at least 6 characters.',
};

const friendlyError = (e) => FRIENDLY_ERRORS[e.code] || e.message;

// Failures coming back from a web redirect sign-in (Google / Apple on web).
const REDIRECT_ERRORS = {
  'auth/account-exists-with-different-credential':
    'An account with this email already exists using a different sign-in method. Sign in the way you did originally (e.g. Google, Apple, or email and password).',
  'auth/network-request-failed': 'Sign-in could not reach the server. Check your internet connection and try again.',
  'auth/user-disabled': 'This account has been disabled. Please contact support.',
  'auth/web-storage-unsupported':
    'Your browser is blocking the storage sign-in needs (often private browsing or blocked cookies). Allow cookies for this site or try another browser.',
  'auth/unauthorized-domain': 'Sign-in is not enabled for this web address. Please use horsemath.app.',
  'auth/internal-error': 'Something went wrong signing in. Please try again.',
};
const REDIRECT_CANCEL_CODES = ['auth/popup-closed-by-user', 'auth/cancelled-popup-request', 'auth/redirect-cancelled-by-user'];

export default function LoginScreen() {
  const [fontsLoaded] = useFonts({ Arvo_700Bold });
  const [mode, setMode] = useState('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [appleLoading, setAppleLoading] = useState(false);
  const [appleWebLoading, setAppleWebLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  // Render the Apple button synchronously on iOS — App Review must always see it.
  // (expo-apple-authentication's isAvailableAsync returns true unconditionally on
  // iOS 13+, so the previous async gate only added a mount-time race.)
  const appleAvailable = Platform.OS === 'ios' && !!AppleAuthentication;

  // Web: if a Google/Apple redirect sign-in failed, say so instead of silently
  // landing back here.
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    let live = true;
    handleWebRedirectResult().then(({ error }) => {
      if (!live || !error || REDIRECT_CANCEL_CODES.includes(error.code)) return;
      console.warn('Redirect sign-in failed:', error.code, error.message);
      setErrorMsg(REDIRECT_ERRORS[error.code] || `Sign-in failed (${error.code || 'unknown error'}). Please try again.`);
    });
    return () => { live = false; };
  }, []);

  if (!fontsLoaded) return null;

  const handleAppleSignIn = async () => {
    setErrorMsg('');
    setAppleLoading(true);
    try {
      await signInWithApple();
    } catch (e) {
      // ERR_REQUEST_CANCELED = user dismissed the Apple sheet; ignore.
      if (e?.code !== 'ERR_REQUEST_CANCELED' && e?.code !== 'ERR_CANCELED') {
        setErrorMsg(e.message || 'Apple sign-in failed.');
        Alert.alert('Sign-in failed', e.message || 'Apple sign-in failed.');
      }
    } finally {
      setAppleLoading(false);
    }
  };

  const handleAppleWebSignIn = async () => {
    setErrorMsg('');
    setAppleWebLoading(true);
    try {
      await signInWithAppleWeb();
    } catch (e) {
      const cancelCodes = ['auth/popup-closed-by-user', 'auth/cancelled-popup-request'];
      if (!cancelCodes.includes(e?.code)) {
        setErrorMsg(e.message || 'Apple sign-in failed.');
        Alert.alert('Sign-in failed', e.message || 'Apple sign-in failed.');
      }
    } finally {
      setAppleWebLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setErrorMsg('');
    setGoogleLoading(true);
    try {
      await signInWithGoogle();
    } catch (e) {
      // Treat user-initiated cancels (native and web) as a no-op, not an error.
      const cancelCodes = [
        statusCodes.SIGN_IN_CANCELLED,
        'auth/popup-closed-by-user',
        'auth/cancelled-popup-request',
      ].filter(Boolean);
      if (!cancelCodes.includes(e?.code)) {
        setErrorMsg(e.message || 'Google sign-in failed.');
        Alert.alert('Sign-in failed', e.message || 'Google sign-in failed.');
      }
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleEmailSubmit = async () => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail || !password) {
      setErrorMsg('Please enter both an email and password.');
      return;
    }
    setErrorMsg('');
    setLoading(true);
    try {
      if (mode === 'signup') {
        await signUpWithEmail(trimmedEmail, password);
      } else {
        await signInWithEmail(trimmedEmail, password);
      }
    } catch (e) {
      setErrorMsg(friendlyError(e));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      Alert.alert('Enter your email', 'Type your email above first, then tap "Forgot password?".');
      return;
    }
    try {
      await resetPassword(trimmedEmail);
      Alert.alert('Check your email', `A password reset link has been sent to ${trimmedEmail}.`);
    } catch (e) {
      Alert.alert('Could not send reset email', friendlyError(e));
    }
  };

  return (
    <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
      <StatusBar style="light" />
      <ScrollView contentContainerStyle={styles.container} keyboardShouldPersistTaps="handled">

        {/* ── Hero Header ── */}
        <View style={styles.hero}>
          {/* Corner brackets */}
          <View style={[styles.corner, styles.cornerTL]} />
          <View style={[styles.corner, styles.cornerTR]} />
          <View style={[styles.corner, styles.cornerBL]} />
          <View style={[styles.corner, styles.cornerBR]} />

          {/* Horse silhouette */}
          <Image
            source={require('../../assets/horse-silhouette.png')}
            style={styles.horseSilhouette}
          />

            <Text style={styles.star}>★</Text>

          {/* App title */}
          <Text style={styles.heroAppName}>Horse <Text style={styles.heroAppNameAccent}>Math</Text></Text>
          <Text style={styles.heroTitle}>Herd & Breeding{'\n'}Management</Text>

          {/* Powered by row */}
          <View style={styles.poweredRow}>
            <View style={styles.poweredLine} />
            <Text style={styles.poweredText}>POWERED BY GINGERBREAD RANCH</Text>
            <View style={styles.poweredLine} />
          </View>
        </View>

        {/* ── Sign-in Form ── */}
        <View style={styles.body}>
          <Text style={styles.formLabel}>
            {mode === 'signup' ? 'Create an account' : 'Sign in to your account'}
          </Text>

          <TextInput
            style={styles.input}
            placeholder="Email"
            placeholderTextColor="#BDBDBD"
            autoCapitalize="none"
            autoCorrect={false}
            keyboardType="email-address"
            value={email}
            onChangeText={setEmail}
          />
          <TextInput
            style={styles.input}
            placeholder="Password"
            placeholderTextColor="#BDBDBD"
            secureTextEntry
            value={password}
            onChangeText={setPassword}
          />

          {mode === 'signin' && (
            <TouchableOpacity onPress={handleForgotPassword} style={styles.forgotLink}>
              <Text style={styles.forgotLinkText}>Forgot password?</Text>
            </TouchableOpacity>
          )}

          {errorMsg ? (
            <Text style={{ color: RED, fontSize: 13, textAlign: 'center', marginBottom: 10, width: '100%', maxWidth: 320 }}>{errorMsg}</Text>
          ) : null}

          <TouchableOpacity style={styles.primaryButton} onPress={handleEmailSubmit} disabled={loading}>
            {loading ? (
              <ActivityIndicator color="#FFFFFF" />
            ) : (
              <Text style={styles.primaryButtonText}>
                {mode === 'signup' ? 'Create Account' : 'Sign In'}
              </Text>
            )}
          </TouchableOpacity>

          <TouchableOpacity onPress={() => { setMode(mode === 'signup' ? 'signin' : 'signup'); setErrorMsg(''); }} style={styles.switchModeLink}>
            <Text style={styles.switchModeText}>
              {mode === 'signup' ? 'Already have an account? Sign in' : "Don't have an account? Sign up"}
            </Text>
          </TouchableOpacity>

          <View style={styles.dividerRow}>
            <View style={styles.dividerLine} />
            <Text style={styles.dividerText}>or</Text>
            <View style={styles.dividerLine} />
          </View>

          <TouchableOpacity style={styles.googleButton} onPress={handleGoogleSignIn} disabled={googleLoading}>
            {googleLoading ? (
              <ActivityIndicator color={TEXT} />
            ) : (
              <Text style={styles.googleButtonText}>Sign in with Google</Text>
            )}
          </TouchableOpacity>

          {appleAvailable && (
            <TouchableOpacity
              style={[styles.googleButton, { backgroundColor: '#000', borderColor: '#000', marginTop: 12 }]}
              onPress={handleAppleSignIn}
              disabled={appleLoading}
            >
              {appleLoading ? (
                <ActivityIndicator color="#FFFFFF" />
              ) : (
                <Text style={[styles.googleButtonText, { color: '#FFFFFF' }]}>{''} Sign in with Apple</Text>
              )}
            </TouchableOpacity>
          )}

          {Platform.OS === 'web' && (
            <TouchableOpacity
              style={[styles.googleButton, { backgroundColor: '#000', borderColor: '#000', marginTop: 12 }]}
              onPress={handleAppleWebSignIn}
              disabled={appleWebLoading}
            >
              {appleWebLoading ? (
                <ActivityIndicator color="#FFFFFF" />
              ) : (
                <Text style={[styles.googleButtonText, { color: '#FFFFFF' }]}>Sign in with Apple</Text>
              )}
            </TouchableOpacity>
          )}
        </View>

        <View style={{ paddingHorizontal: 24, paddingBottom: 24, paddingTop: 8, flexDirection: 'row', justifyContent: 'center', flexWrap: 'wrap', gap: 4 }}>
          <Text style={{ fontSize: 12, color: MUTED }}>By signing in you agree to our</Text>
          <TouchableOpacity onPress={() => Linking.openURL('https://horsemath.app/terms')}>
            <Text style={{ fontSize: 12, color: BURGUNDY, textDecorationLine: 'underline' }}>Terms of Service</Text>
          </TouchableOpacity>
          <Text style={{ fontSize: 12, color: MUTED }}>and</Text>
          <TouchableOpacity onPress={() => Linking.openURL('https://horsemath.app/privacy')}>
            <Text style={{ fontSize: 12, color: BURGUNDY, textDecorationLine: 'underline' }}>Privacy Policy</Text>
          </TouchableOpacity>
        </View>

      </ScrollView>
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  container: {
    flexGrow: 1,
    backgroundColor: BG,
  },

  // ── Hero ──
  hero: {
    backgroundColor: BURGUNDY,
    paddingTop: 56,
    paddingBottom: 32,
    paddingHorizontal: 32,
    alignItems: 'center',
    overflow: 'hidden',
  },
  corner: {
    position: 'absolute',
    width: 16,
    height: 16,
  },
  cornerTL: {
    top: 18,
    left: 18,
    borderTopWidth: 1.5,
    borderLeftWidth: 1.5,
    borderColor: '#E8A23B',
    opacity: 0.6,
  },
  cornerTR: {
    top: 18,
    right: 18,
    borderTopWidth: 1.5,
    borderRightWidth: 1.5,
    borderColor: '#E8A23B',
    opacity: 0.6,
  },
  cornerBL: {
    bottom: 18,
    left: 18,
    borderBottomWidth: 1.5,
    borderLeftWidth: 1.5,
    borderColor: '#E8A23B',
    opacity: 0.6,
  },
  cornerBR: {
    bottom: 18,
    right: 18,
    borderBottomWidth: 1.5,
    borderRightWidth: 1.5,
    borderColor: '#E8A23B',
    opacity: 0.6,
  },
  horseSilhouette: {
    position: 'absolute',
    right: -10,
    bottom: -8,
    width: 140,
    height: 140,
    opacity: 0.45,
  },
  star: {
    fontSize: 52,
    color: '#E8A23B',
    marginBottom: 8,
    lineHeight: 60,
  },
  heroAppName: {
    fontSize: 32,
    color: '#fff',
    letterSpacing: -0.3,
    marginBottom: 4,
    fontFamily: 'Arvo_700Bold',
  },
  heroAppNameAccent: {
    color: '#E8A23B',
    fontFamily: 'Arvo_700Bold',
  },
  heroTitle: {
    fontSize: 13,
    fontWeight: '400',
    fontStyle: 'italic',
    color: CREAM,
    textAlign: 'center',
    letterSpacing: 0.4,
    opacity: 0.85,
    marginBottom: 16,
    fontFamily: 'Georgia',
    lineHeight: 20,
  },
  poweredRow: {
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
    gap: 8,
  },
  poweredLine: {
    flex: 1,
    height: 0.5,
    backgroundColor: '#E8A23B',
    opacity: 0.45,
  },
  poweredText: {
    fontSize: 9,
    color: '#E8A23B',
    letterSpacing: 2,
    fontWeight: '600',
    opacity: 0.85,
  },

  // ── Form ──
  body: {
    paddingHorizontal: 32,
    paddingTop: 28,
    paddingBottom: 40,
    alignItems: 'center',
  },
  formLabel: {
    fontSize: 15,
    color: MUTED,
    marginBottom: 20,
    textAlign: 'center',
  },
  input: {
    width: '100%',
    maxWidth: 320,
    borderWidth: 1,
    borderColor: BORDER,
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 14,
    fontSize: 15,
    color: TEXT,
    backgroundColor: '#FFFFFF',
    marginBottom: 12,
  },
  forgotLink: {
    width: '100%',
    maxWidth: 320,
    alignItems: 'flex-end',
    marginBottom: 12,
  },
  forgotLinkText: {
    fontSize: 13,
    color: MUTED,
  },
  primaryButton: {
    backgroundColor: BURGUNDY,
    borderRadius: 8,
    paddingVertical: 14,
    width: '100%',
    maxWidth: 320,
    alignItems: 'center',
  },
  primaryButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: CREAM,
  },
  switchModeLink: {
    marginTop: 16,
  },
  switchModeText: {
    fontSize: 14,
    color: RED,
    fontWeight: '600',
  },
  dividerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
    maxWidth: 320,
    marginVertical: 24,
  },
  dividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: BORDER,
  },
  dividerText: {
    marginHorizontal: 12,
    color: MUTED,
    fontSize: 13,
  },
  googleButton: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: BORDER,
    borderRadius: 8,
    paddingVertical: 14,
    width: '100%',
    maxWidth: 320,
    alignItems: 'center',
  },
  googleButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: TEXT,
  },
});
